import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { SettingsCard } from "@/components/ui/settings-card";
import { PrimaryButton, SecondaryButton, StatusCircle, helpCls, labelCls } from "@/components/agent/ui";

export const FieldRow = ({ label, detail, value, status, testid, lead, trail }) => (
  <div className="flex items-center gap-4" data-testid={testid} data-status={status}>
    {lead}
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium">{label}</p>
      {detail && <p className="text-xs text-muted-foreground">{detail}</p>}
    </div>
    {value != null && <span className={cn("shrink-0 text-right text-sm tabular-nums", status === "failed" && "text-destructive")}>{value}</span>}
    {trail ?? (status ? <StatusCircle status={status} /> : <span className="size-5 shrink-0" />)}
  </div>
);

export const Section = ({ summary, testid, ...props }) => (
  <SettingsCard divide testid={testid} {...props}
    action={summary && <span className="text-xs tabular-nums text-muted-foreground" data-testid={`${testid}-summary`}>{summary}</span>} />
);

const EmailPreview = ({ preview, loading, error }) => (
  <div className="grid gap-2" data-testid="decision-email-preview">
    <span className={labelCls}>Email to the applicant</span>
    <div className="rounded-lg border bg-panel p-3 text-sm">
      {error ? <p className="text-destructive" data-testid="decision-email-error">{error}</p> : !preview ? (
        <p className="flex items-center gap-2 text-muted-foreground"><Loader2 className="size-4 animate-spin" /> Writing the email…</p>
      ) : (
        <div className={cn("flex flex-col gap-2 transition-opacity", loading && "opacity-50")}>
          <p className="text-xs text-muted-foreground" data-testid="decision-email-to">
            {preview.to ? <>To {preview.to} · {preview.subject}</> : "No email address yet, so this won't be sent."}
          </p>
          <div className="flex max-h-56 flex-col gap-2 overflow-y-auto" data-testid="decision-email-body">
            {preview.body.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
            <p className="text-muted-foreground">{preview.from_name}</p>
          </div>
        </div>
      )}
    </div>
    {loading && preview && <span className={cn(helpCls, "flex items-center gap-1.5")}><Loader2 className="size-3 animate-spin" /> Rewriting with your reason…</span>}
  </div>
);

const useEmailPreview = (url, decision, reason, open) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.post(url, { decision, reason: reason.trim() });
        if (live) setPreview({ ...data, reason: reason.trim() });
      } catch (err) {
        if (live) setError(errorText(err));
      } finally {
        if (live) setLoading(false);
      }
    }, reason.trim() ? 900 : 0);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [url, decision, reason, open]);
  return { preview, loading, error };
};

export const ConfirmDialog = ({ open, title, description, onClose, onConfirm, confirmLabel, decision, previewUrl, withReason, testid }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const { preview, loading: fetching, error } = useEmailPreview(previewUrl, decision, reason, open);
  const loading = fetching || (!!preview && preview.reason !== reason.trim());
  const confirm = async () => {
    setBusy(true);
    await onConfirm(reason.trim(), preview?.body);
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl" data-testid={testid}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {withReason && (
          <label className="grid gap-2">
            <span className={labelCls}>Reason <span className="font-normal text-muted-foreground">(optional)</span></span>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={3}
              placeholder="For example: the move-in date doesn't work." data-testid="decline-reason-input" />
            <span className={helpCls}>Stored with the criteria version used. If you add one, the email is rewritten around it.</span>
          </label>
        )}
        <EmailPreview preview={preview} loading={loading} error={error} />
        <DialogFooter>
          <SecondaryButton size="default" onClick={onClose} testid={`${testid}-cancel`}>Cancel</SecondaryButton>
          <PrimaryButton size="default" onClick={confirm} isLoading={busy} disabled={loading || !preview} testid={`${testid}-confirm`}>
            {preview?.to ? `${confirmLabel} and send email` : confirmLabel}
          </PrimaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RequestInfoDialog = ({ open, steps, onClose, onSend }) => {
  const [step, setStep] = useState(steps[0]?.key || "");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    await onSend(step, message.trim());
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="request-info-dialog">
        <DialogHeader>
          <DialogTitle>Request info</DialogTitle>
          <DialogDescription>The applicant gets an email with a link that opens only this step.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <span className={labelCls}>Step to fix</span>
          <Select value={step} onValueChange={setStep}>
            <SelectTrigger size="input" className="h-9 w-full" data-testid="request-info-step"><SelectValue /></SelectTrigger>
            <SelectContent>
              {steps.map((s) => <SelectItem key={s.key} value={s.key} data-testid={`request-info-step-${s.key}`}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <label className="grid gap-2">
          <span className={labelCls}>Message</span>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500} rows={4} data-testid="request-info-message" />
        </label>
        <DialogFooter>
          <SecondaryButton size="default" onClick={onClose} testid="request-info-cancel">Cancel</SecondaryButton>
          <PrimaryButton size="default" onClick={send} isLoading={busy} disabled={!step || !message.trim()} testid="request-info-send">Send request</PrimaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
